"use client";

import { useState } from "react";
import type {
  KeyIndicatorEntry,
  KeyIndicatorsBarComponent,
  KeyIndicatorsBarContent,
} from "../../types";
import { useT } from "../../runtime/RendererRuntime";
import { KeyIndicatorsModal, TrendIcon, couleurVariation } from "./KeyIndicatorsModal";
import { polariteIndicateur } from "./trendSemantics";

function IndicatorItem({ indicator }: { indicator: KeyIndicatorEntry }) {
  const trend = indicator.trend ?? "flat";
  // La couleur juge le SENS du mouvement pour cet indicateur, pas sa direction : une baisse
  // de l'inflation est verte, une hausse du dollar face au franc est rouge, et le taux
  // directeur, que la Banque fixe elle-même, reste gris dans les deux sens.
  const color = couleurVariation(trend, polariteIndicateur(indicator.label));
  // Espacement SYMÉTRIQUE autour du trait séparateur : l'item ne porte plus de
  // px-6 propre (qui s'additionnait au padding de l'item suivant : 48px après le
  // trait contre 24px avant). Le trait porte tout : gap-3 (12) + ml-3 (12) = 24px
  // à sa gauche, mr-6 = 24px à sa droite.
  return (
    <li className="flex items-center gap-3 whitespace-nowrap py-2.5 text-sm">
      <span className="font-semibold uppercase tracking-wide text-[#005CA9]">{indicator.label}</span>
      <span className="font-bold tabular-nums text-[#005CA9]">
        {indicator.value}
        {indicator.unit ? <span className="ml-1 font-medium text-[#005CA9]">{indicator.unit}</span> : null}
      </span>
      {indicator.delta ? (
        <span className={`flex items-center gap-1 font-semibold tabular-nums ${color}`}>
          <TrendIcon trend={trend} className="h-3.5 w-3.5" />
          {indicator.delta}
        </span>
      ) : null}
      <span aria-hidden className="ml-3 mr-6 h-3 w-px shrink-0 bg-[#005CA9]/20" />
    </li>
  );
}

/** Slot-friendly inner ticker — no full-bleed wrapper. Use this when the bar is mounted inside
 * a parent that already spans the viewport (e.g. the fixed BelowHeaderSlot). */
export function KeyIndicatorsBarTicker({ content }: { content: KeyIndicatorsBarContent }) {
  const { indicators, ariaLabel, details, viewAllLabel, viewAllHref } = content;
  const tr = useT();
  // Un clic sur la barre ouvre la vue complète : le défilement n'obligeait plus qu'à
  // attendre son tour pour lire le taux directeur.
  const [open, setOpen] = useState(false);
  if (!indicators || indicators.length === 0) return null;
  // Duplicate so the marquee loops seamlessly without a visible jump.
  const trackItems = [...indicators, ...indicators];
  const titre = ariaLabel ?? tr("keyIndicatorsAria");
  return (
    /* No top border — the header above already paints a bottom separator, so adding our own
     * on top doubles up to 2px (visible on mobile where the lines are thicker per density). */
    <section
      aria-label={titre}
      className="bcc-key-indicators-bar relative overflow-hidden border-b border-[#005CA9]/15 bg-[#F4F8FC] text-[#005CA9]"
    >
      {/* Deux arrêts, et deux seulement : le SURVOL de la souris (règle CSS portée par la
          section, cf. globals.css) et l'ouverture de la vue complète, un bandeau qui court
          derrière un panneau de lecture agitant le fond pour rien. Ni le clic ni le focus
          clavier n'arrêtent quoi que ce soit. `prefers-reduced-motion` coupe toujours
          l'animation, pour qui a réglé son système ainsi. */}
      <div
        className="bcc-key-indicators-track flex min-w-max"
        style={open ? { animationPlayState: "paused" } : undefined}
      >
        <ul className="flex" role="presentation">
          {trackItems.map((indicator, idx) => (
            <IndicatorItem key={`${indicator.label}-${idx}`} indicator={indicator} />
          ))}
        </ul>
      </div>
      {/* TOUTE la barre ouvre la vue complète : superposition transparente, donc un seul
          élément interactif, accessible au clavier (Tab puis Entrée/Espace), sans perturber
          la lecture du contenu par lecteur d'écran (le texte reste dans la liste dessous). */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={tr("openKeyIndicators")}
        className="absolute inset-0 h-full w-full cursor-pointer bg-transparent transition hover:bg-[#005CA9]/[0.04]"
      />
      <KeyIndicatorsModal
        open={open}
        onClose={() => setOpen(false)}
        title={titre}
        indicators={indicators}
        details={details}
        viewAllLabel={viewAllLabel}
        viewAllHref={viewAllHref}
      />
    </section>
  );
}

/** In-page renderer (legacy path: bar embedded in a page's component list).
 * Wraps the ticker in .bcc-full-bleed so it spans the viewport from inside a centered container. */
export function KeyIndicatorsBarRenderer({ component }: { component: KeyIndicatorsBarComponent }) {
  const { content } = component;
  if (!content?.indicators?.length) return null;
  return (
    <div className="bcc-full-bleed">
      <KeyIndicatorsBarTicker content={content} />
    </div>
  );
}
